import {ViewOptions} from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import EditPrimerTabState from 'analyses/EditPrimerTabState';
import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {CodonTranslationTable} from 'biojs/BioConstants';
import {openOrCreateBiochemicalPropertiesTab} from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {MapType} from 'constants/MapsConstants';
import {convertStrandToDisplayName, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import {getDefaultCopyModeForSequence} from 'molbio/CopyUtil';
import {blastSequence} from 'molbio/ExternalLib';
import {PolymerLinearMapState} from 'molbio/maps/MapStates';
import LinearOptions from 'molbio/maps/options/LinearOptions';
import {MapSelectorProps} from 'molbio/maps/options/MapSelector';
import SequenceOptionsView, {SequenceOptionsBaseProps} from 'molbio/maps/options/SequenceOptionsView';
import SimplifiedLinearOptions from 'molbio/maps/options/SimplifiedLinearOptions';
import {ZoomSliderProps} from 'molbio/maps/options/Sliders';
import {isCreateNewPartFromSelectionDisabled} from 'molbio/parts/isCreateNewPartFromSelectionDisabled';
import {linearPaginate} from 'molbio/pdf/Paginate';
import {trackSequenceMapClickAnalyze, trackSequenceMapClickCreate} from 'molbio/SequenceAnalyticsHelper';
import {GenericLinearMapTabState} from 'molbio/tabstates/SequenceLinearMapTabState';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {runBLASTModalForSelection} from 'search/blast/BLASTUtil';
import {validateBLASTSequence} from 'search/blast/validation';
import {Operator} from 'search/SearchConstants';
import {FeatureCollectionName} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export interface LinearSequenceOptionsProps extends SequenceOptionsBaseProps {
  isRnaSequence: boolean;
  isSingleStranded: boolean;
  selectionLength: number;
  createAnnotation: () => void;
  createPrimer: (strand: StrandOfDoubleStranded) => void;
  createProtein: (strand: StrandOfDoubleStranded) => void;
  createTranslation: (strand: StrandOfDoubleStranded) => void;
  createSequence: (newSequenceType?: SequenceType, strand?: StrandOfDoubleStranded) => void;
  createPart: () => void;
  runBenchlingBlast: () => void;
  runNcbiBlast: () => void;
  runPrimer3: () => void;
  analyzeAsTranslation: () => void;
  handleOptimizeCodonsFromSelectionClick: (strand: StrandOfDoubleStranded) => void;
  isBenchlingBlastable: boolean;
  isNcbiBlastable: boolean;
  isCreateNewPartFromSelectionDisabled: boolean;
  onClickCreate: () => void;
  onClickAnalyze: () => void;
  isOligo: boolean;
  onCopy: () => void;
  zoomSliderProps: ZoomSliderProps;
  allowFeatureEdits: boolean;
  isCopyable: boolean;
}

export class LinearMapTabOptionsView extends SequenceOptionsView<
  PolymerLinearMapState<Sequence>,
  GenericLinearMapTabState
> {
  previewSourceName = 'linear';
  previewPaginate = linearPaginate;

  trackedFeatureCollections(): Array<FeatureCollectionName> {
    const trackedFeatureCollections: Array<FeatureCollectionName> = [
      'annotations',
      'sequencePrimers',
      'translations',
      'transcriptions',
    ];
    if (BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      trackedFeatureCollections.push('parts');
    }
    return trackedFeatureCollections;
  }

  override getProps(): LinearSequenceOptionsProps {
    const sequence = this.mapstate.sequence();
    const selection = sequence.selection();

    return {
      ...super.getProps(),
      allowFeatureEdits: sequence.allowFeatureEdits(),
      isOligo: sequence.isOligo(),
      isRnaSequence: sequence.isRNASequence(),
      isSingleStranded: sequence.isSingleStranded(),
      onCopy: () => {
        sequence.handleCopy(getDefaultCopyModeForSequence(sequence));
      },
      selectionLength: sequence.selection().length(),
      sequenceId: sequence.id,
      zoomSliderProps: {
        min: this.mapstate.get('minZoom'),
        max: this.mapstate.get('maxZoom'),
        value: this.mapstate.get('zoom'),
        step: this.mapstate.get('stepZoom'),
        disabled: false,
        onChange: (zoom: number) => this.mapstate.setZoom(zoom),
        zoomSmaller: () => this.mapstate.changeZoomByStep(-10),
        zoomBigger: () => this.mapstate.changeZoomByStep(10),
      },
      createProtein: (strand: StrandOfDoubleStranded) => {
        sequence.handleCreateProteinFromSelectionClick(strand);
      },
      createAnnotation: () => {
        analytics.track('annotation', 'open_annotation_panel', {
          product: ANALYTICS_MODULE.MOLBIO,
          openedFrom: 'create_options',
          sequence_id: sequence.get('id'),
        });
        selection.trigger('annotation');
      },
      createPrimer: (strand: StrandOfDoubleStranded) => {
        analytics.track('primer', 'open_edit_primer_tab', {
          product: ANALYTICS_MODULE.MOLBIO,
          createdFrom: 'create_options',
          direction: convertStrandToDisplayName(strand).toLowerCase(),
          sequence_id: sequence.get('id'),
        });
        const tab = EditPrimerTabState.createFromSelection(sequence, strand);
        sequence.fileState().buffers().addTab(tab);
      },
      createTranslation: (strand: StrandOfDoubleStranded) => {
        analytics.track('translation', 'open_translation_panel', {
          product: ANALYTICS_MODULE.MOLBIO,
          openedFrom: 'create_options',
          strand,
          sequence_id: sequence.get('id'),
        });
        sequence.createTranslation(strand);
      },
      runBenchlingBlast: () => runBLASTModalForSelection(selection),
      runNcbiBlast: () => {
        blastSequence(sequence);
      },
      runPrimer3: () => {
        sequence.handlePrimer3FromSelectionClick();
      },
      analyzeAsTranslation: () => {
        openOrCreateBiochemicalPropertiesTab(sequence, CodonTranslationTable.STANDARD);
      },
      handleOptimizeCodonsFromSelectionClick: (strand: StrandOfDoubleStranded) => {
        sequence.handleOptimizeCodonsFromSelectionClick(strand);
      },
      createSequence: (sequenceType?: SequenceType, strand?: StrandOfDoubleStranded) => {
        sequence.handleCreateFromSelectionClick(sequenceType, strand);
      },
      createPart: () => {
        sequence.handleCreatePartFromSelectionClick();
      },
      isCopyable: selection.isCopyable(),
      isBenchlingBlastable:
        !selection.isEmpty() &&
        validateBLASTSequence(selection.getSelectionResidues(), Operator.BLASTN).isValid,
      isNcbiBlastable: selection.isBlastable(),
      isCreateNewPartFromSelectionDisabled: isCreateNewPartFromSelectionDisabled(sequence),
      onClickCreate: () => {
        trackSequenceMapClickCreate(sequence);
      },
      onClickAnalyze: () => {
        trackSequenceMapClickAnalyze(sequence);
      },
    };
  }

  handleBigSequence(): void {
    const isReallyBig = this.mapstate.sequence().isReallyBig();
    this.model.get('visibilityOptions').getOption(vo.ORFS.name)!.set('disabled', isReallyBig);
  }

  override initialize(options?: ViewOptions<GenericLinearMapTabState>): void {
    super.initialize(options);

    // Set up mapstate and model listeners.
    this.listenTo(this.mapstate, 'change:zoom change:minZoom change:maxZoom change:stepZoom', () =>
      this.render()
    );

    // Set up sequence listeners.
    const sequence = this.mapstate.sequence();
    this.listenTo(sequence.selection(), 'change:start change:end', () => this.render());
    this.listenTo(sequence, 'change:currentVersion change:writable change:permissions', () => this.render());
    this.listenTo(sequence._sequence(), 'change:locked', () => this.render());
  }

  override remove(): this {
    ReactDOM.unmountComponentAtNode(this.el);
    super.remove();
    return this;
  }

  override render(): this {
    renderToDOM(<LinearOptions {...this.getProps()} />, this.el);
    return this;
  }
}

export type SimplifiedLinearSequenceOptionsProps = LinearSequenceOptionsProps & MapSelectorProps;

export class SimplifiedLinearMapTabOptionsView extends LinearMapTabOptionsView {
  override getProps(): SimplifiedLinearSequenceOptionsProps {
    return {
      ...super.getProps(),
      mapType: MapType.LINEAR_SEQUENCE,
      onSelectMapType: (mapType: MapType) => {
        // Emit special event that we can listen to from React.
        this.mapstate.sequence().trigger('changeMapType', mapType);
      },
      isCircular: this.mapstate.sequence().circular(),
    };
  }

  override render(): this {
    renderToDOM(<SimplifiedLinearOptions {...this.getProps()} />, this.el);
    return this;
  }
}
